import React, { useCallback, useEffect, useState } from "react";
import { Loader, Message } from "rsuite";
import { QRCodeSVG } from "qrcode.react";
import ReactModalWrap from "../common/ReactModalWrap";
import Button from "../common/Button";
import modalStyles from "./Modal.module.scss";
import styles from "./ServiceShareModal.module.scss";
import { parseTrackingUrlResponse } from "../../constants/serviceShare";
import { useGlobalContext } from "../../GlobalContext";
import NetworkService from "../../NetworkService";
import { getRideOrderApiErrorMessage } from "../ride-order/rideOrderPayload";
import { toasterFunction } from "../utils";
import openInBrowserIcon from "../../static/images/ic-open-in-browser.svg";

function ServiceShareModal({ isOpen, close, rideId = null }) {
  const { user: userState } = useGlobalContext();
  const [admin] = userState;
  const companyAdminId = admin?.user?.id;

  const [isCopying, setIsCopying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      setShareUrl(null);
      setError(null);
      return;
    }

    if (!rideId) {
      setError("Service ID is missing.");
      return;
    }

    if (!companyAdminId || !NetworkService.jwtToken) {
      setError("Missing company admin session.");
      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setShareUrl(null);
    setError(null);

    NetworkService.getCompanyRideTrackingUrl(rideId, companyAdminId)
      .then((response) => {
        if (cancelled) return;

        const url = parseTrackingUrlResponse(response);
        if (!url) {
          throw { data: { message: "Tracking URL not available." } };
        }

        setShareUrl(url);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(getRideOrderApiErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [companyAdminId, isOpen, rideId]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;

    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(shareUrl);
      toasterFunction(
        <Message closable showIcon type="success">
          Link copied to clipboard.
        </Message>
      );
    } catch {
      toasterFunction(
        <Message closable showIcon type="error">
          Unable to copy link. Please copy it manually.
        </Message>
      );
    } finally {
      setIsCopying(false);
    }
  }, [shareUrl]);

  const handleOpenInNewTab = useCallback(() => {
    if (!shareUrl) return;
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  }, [shareUrl]);

  return (
    <ReactModalWrap isOpen={isOpen} close={close}>
      <div className={modalStyles.content}>
        <div className={styles.content}>
          <div className={styles.header}>Share service</div>
          <div className={styles.subHeader}>
            {rideId ? (
              <>
                Share service <strong>#{rideId}</strong> with passengers or colleagues.
              </>
            ) : (
              "Share this service link with passengers or colleagues."
            )}
          </div>

          {isLoading ? (
            <div className={styles.loadingState}>
              <Loader size="md" content="Loading share link..." />
            </div>
          ) : error ? (
            <div className={styles.errorState}>{error}</div>
          ) : shareUrl ? (
            <>
              <div className={styles.linkSection}>
                <label className={styles.linkLabel} htmlFor="service-share-link">
                  Service link
                </label>
                <div className={styles.linkRow}>
                  <input
                    id="service-share-link"
                    className={styles.linkInput}
                    type="text"
                    value={shareUrl}
                    readOnly
                    onFocus={(event) => event.target.select()}
                  />
                  <div className={styles.linkActions}>
                    <Button
                      className={styles.openTabButton}
                      onClick={handleOpenInNewTab}
                      aria-label="Open link in new tab"
                    >
                      <img src={openInBrowserIcon} alt="" className={styles.openTabIcon} />
                    </Button>
                    <Button
                      type="button"
                      variant="green"
                      className={styles.copyButton}
                      disabled={isCopying}
                      onClick={handleCopy}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </div>

              <div className={styles.qrSection}>
                <div className={styles.qrCard}>
                  <QRCodeSVG
                    value={shareUrl}
                    size={180}
                    level="M"
                    bgColor="#FFFFFF"
                    fgColor="#171B1F"
                    title="Service info QR code"
                  />
                </div>
                <p className={styles.qrHint}>Scan the QR code to open the service info page on a mobile device.</p>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </ReactModalWrap>
  );
}

export default ServiceShareModal;
